import { display, height, styled } from "@mui/system";
import TextField from "@mui/material/TextField";
import { brandFontFamily, brandFontSize, brandInputTextColor } from "../../../utils/variables";
import { FormControl } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import FieldLabel from "../FieldLabel";

const CustomTextField = styled(TextField)(
  ({ isDefaultField, error = false, height, fullHeight, hasSearch }) => ({
    "& .MuiOutlinedInput-root.Mui-disabled": {
      backgroundColor: "#f5f5f5 !important",
      boxShadow: "none",
      borderRadius: isDefaultField ? "8px" : "4px !important",
      cursor: "not-allowed",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "#9f9f9fff !important",
      WebkitTextFillColor: "#9f9f9fff",
    },
    "& .MuiOutlinedInput-notchedOutline.Mui-disabled": {},
    "& .MuiInputLabel-root": {
      fontSize: brandFontSize,
      fontWeight: 400,
      fontFamily: brandFontFamily,
    },
    "& .MuiInputBase-root": {
      color: brandInputTextColor,
      fontFamily: brandFontFamily,
      borderRadius: isDefaultField ? "8px" : "4px !important",
      padding: "0px",
      width: "100% !important",
      fontSize: brandFontSize,
      fontWeight: 300,
      backgroundColor: hasSearch ? "#F5F5F5" : "#FFF",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "15px",
      backgroundColor: hasSearch ? "#F5F5F5" : "#fff",
      padding: "0 4px",
    },
    "& .MuiInputBase-input": {
      padding: hasSearch ? "10px 14px 10px 4px !important" : "14px!important",
      fontSize: brandFontSize,
      height: fullHeight ? "60% !important" : height ? height : 0,
      "@media (max-width: 640px)": {
        padding: hasSearch ? "7px 14px 7px 4px !important" : "9px 14px !important",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      ...(hasSearch
        ? { border: "none !important", boxShadow: "none" }
        : !isDefaultField && { border: "none", boxShadow: "1px 1px 4px 1px #00000021" }),
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: hasSearch ? "none !important" : `1.5px solid ${error ? "red" : "#0F40AE"}`,
      boxShadow: "none",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: error ? "red" : "#0F40AE",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiInputAdornment-root i": {
      ...(hasSearch && { color: "#9E9E9E !important" }),
    },
  }),
);

const BaseTextField = ({
  labelShrink = true,
  label,
  className,
  value,
  onChange,
  optional = true,
  requirement,
  fullHeight = true,
  variant = "outlined",
  hint,
  bottomRightMsg,
  labelRightMsg,
  // Extra props for the label above the input (labelShrink={false}), e.g. an `sx`
  // that lets a long label wrap: MUI clips labels on one line by default.
  labelProps,
  error,
  errorMsg,
  inputRef,
  isDefaultField = true,
  startAdornment, // Pass startAdornment as a prop
  endAdornment, // Pass endAdornment as a prop
  inputProps,
  ...props
}) => {
  // `requirement` is the preferred prop; fall back to the legacy `optional` flag
  // (which only ever chose between "– Optional" and no marker at all).
  const resolvedRequirement = requirement ?? (optional ? "optional" : "none");
  const describedById = errorMsg && error && props.id ? `${props.id}-error` : undefined;

  // `type="float"` is not a real input type, so the browser rendered these rate
  // and hours fields as plain text and let letters and symbols through. Render a
  // text input and reject anything that isn't a positive decimal instead: a
  // native number input would also accept "e", "+" and "-".
  const isFloat = props.type === "float";
  const floatProps = isFloat
    ? {
        type: "text",
        onChange: (event) => {
          const nextValue = event.target.value;
          // Allows "", "12", "12.", ".5" — everything else is dropped, and React
          // restores the previous value because no state change follows.
          if (nextValue !== "" && !/^\d*\.?\d*$/.test(nextValue)) return;
          onChange?.(event);
        },
      }
    : {};

  return (
    <div data-field-error={error ? "true" : undefined} data-field-name={props.name}>
      {!labelShrink && (
        <FieldLabel
          label={label}
          requirement={resolvedRequirement}
          hint={hint}
          endContent={labelRightMsg}
          htmlFor={props.id}
          fullHeight={fullHeight}
          {...labelProps}
        />
      )}
      <CustomTextField
        className={className}
        InputLabelProps={
          {
            // shrink: isDefaultField && labelShrink ? false : labelShrink,
          }
        }
        height={height}
        fullHeight={fullHeight}
        error={!!error}
        inputRef={inputRef}
        value={value}
        onChange={onChange}
        label={labelShrink ? label : null}
        variant={variant}
        isDefaultField={isDefaultField}
        hasSearch={!!startAdornment}
        inputProps={{
          ...inputProps,
          // Brings up the numeric keypad on mobile without the spinner arrows.
          ...(isFloat && { inputMode: "decimal" }),
          "aria-invalid": error ? "true" : undefined,
          "aria-describedby": describedById,
        }}
        InputProps={{
          startAdornment: startAdornment && (
            <InputAdornment style={{ marginLeft: "14px" }} position="start">
              {startAdornment}
            </InputAdornment>
          ),
          endAdornment: endAdornment && (
            <InputAdornment style={{ marginRight: "14px" }} position="end">
              {endAdornment}
            </InputAdornment>
          ),
        }}
        {...props}
        {...floatProps}
      />
      {/* Only when there is something to show — keying this off `errorMsg` being
          defined left an empty strip under every field that declares one, which
          made row spacing uneven against fields that don't. */}
      {((error && errorMsg) || bottomRightMsg) && (
        <div className="flex justify-between mt-1.5">
          {error && (
            <p id={describedById} role="alert" className="text-red-500 w-full">
              {errorMsg}
            </p>
          )}
          {bottomRightMsg && <p className="text-right  w-full">{bottomRightMsg}</p>}
        </div>
      )}
    </div>
  );
};

export default BaseTextField;
